import { CATALOG_CATEGORY_ORDER, CONNECTOR_CATALOG } from "../connectors/catalog.js";
/**
 * The ordered list of connected-service types, in catalog declaration order.
 * Typed as `readonly ConnectedServiceType[]` (the element type is the precise
 * union); runtime order matches the catalog.
 */
export const CONNECTED_SERVICE_TYPES = CONNECTOR_CATALOG.map((entry) => entry.id);
export const CUSTOM_SERVICE_AUTH_SCHEMES = [
    "bearer",
    "api-key-header",
    "basic",
    "query-param",
];
/**
 * Per-service definition data, DERIVED from `CONNECTOR_CATALOG`. Keyed by
 * service type in catalog order. Preserves the previous object shape
 * (label/description/capabilities/defaultCapabilities/credentialFields and an
 * optional oauthFlow) so all readers — including the frontend via
 * `getConnectedServiceDefinition` — are unchanged.
 */
const connectedServiceDefinitionMap = Object.fromEntries(CONNECTOR_CATALOG.map((entry) => [
    entry.id,
    {
        label: entry.label,
        description: entry.description,
        capabilities: entry.capabilities,
        defaultCapabilities: entry.defaultCapabilities,
        credentialFields: entry.credentialFields,
        ...(entry.oauthFlow !== undefined ? { oauthFlow: entry.oauthFlow } : {}),
    },
]));
export function getConnectedServiceDefinition(serviceType) {
    const definition = connectedServiceDefinitionMap[serviceType];
    return {
        serviceType,
        label: definition.label,
        description: definition.description,
        capabilities: [...definition.capabilities],
        defaultCapabilities: [...definition.defaultCapabilities],
        credentialFields: definition.credentialFields.map((field) => ({ ...field })),
        oauthFlow: "oauthFlow" in definition ? definition.oauthFlow : undefined,
    };
}
export function getConnectedServiceCapabilities(serviceType) {
    return [...connectedServiceDefinitionMap[serviceType].capabilities];
}
export function getDefaultConnectedServiceCapabilities(serviceType) {
    return [...connectedServiceDefinitionMap[serviceType].defaultCapabilities];
}
export const CONNECTED_SERVICE_DEFINITIONS = CONNECTED_SERVICE_TYPES.map((serviceType) => getConnectedServiceDefinition(serviceType));
/**
 * Connected-service categories, DERIVED from `CONNECTOR_CATALOG` (membership via
 * each entry's `category`) and `CATALOG_CATEGORY_ORDER` (stable category order +
 * optional intra-category service-order overrides). Output is byte-for-byte
 * identical to the previous hand-authored list and is guarded by
 * `packages/shared/test/connectors/catalog.test.ts`.
 */
export const CONNECTED_SERVICE_CATEGORIES = CATALOG_CATEGORY_ORDER.map((category) => {
    const inCategory = CONNECTOR_CATALOG.filter((entry) => entry.category === category.key).map((entry) => entry.id);
    const services = category.serviceOrder
        ? [...category.serviceOrder].map((id) => id)
        : inCategory;
    return { key: category.key, label: category.label, services };
});
/** Google Workspace capability sub-groups for UI rendering. */
export const GOOGLE_WORKSPACE_CAPABILITY_GROUPS = [
    { key: "gmail", label: "Gmail", prefix: "gmail." },
    { key: "calendar", label: "Calendar", prefix: "calendar." },
    { key: "contacts", label: "Contacts", prefix: "contacts." },
    { key: "drive", label: "Drive", prefix: "drive." },
    { key: "analytics", label: "Analytics", prefix: "analytics." },
    { key: "sheets", label: "Sheets", prefix: "sheets." },
    { key: "docs", label: "Docs", prefix: "docs." },
    { key: "slides", label: "Slides", prefix: "slides." },
];
//# sourceMappingURL=connectedServices.js.map